import type { StateCreator } from 'zustand'
import {
  defaultExtensions,
  parseBcfZip,
  type BcfComment,
  type BcfExtensions,
  type BcfTopic,
  type ImportedViewpoint,
  type ParsedBcf,
} from '@modules/bcf/lib/bcfParser'
import { bcfPersistence } from '@modules/bcf/lib/bcfPersistence'
import { comOrigem, origemRetroactiva } from '@modules/bcf/lib/bcfOrigem'
import {
  viewpointsStore,
  type LocalViewpoint,
} from '@viewer/lib/viewpointsStore'
import type { Viewpoint } from '@viewer/lib/viewpoint'
import { semCena } from '@viewer/lib/viewpointCena'
import { whenRecoveryResolved } from '@core/session/recovery'
import {
  contextoDaCena,
  lerVisualizacaoDaCena,
  type ContextoDeLeitura,
} from '@modules/bcf/lib/bcfReferencial'

/** Hard cap on versions kept per BCF. The 6th import drops the oldest
 * version to free IDB space; versions are full snapshots, so they're
 * the heaviest payload in the local store. */
export const MAX_VERSIONS_PER_BCF = 5

export interface BcfVersion {
  versionId: string
  /** "v1", "v2", ..., auto-assigned, user-renameable. */
  label: string
  filename: string
  importedAt: number
  sizeBytes: number
  parsed: ParsedBcf
}

export interface LoadedBcf {
  localId: string
  /** Display name; defaults to first version's filename or its
   * `sourceProjectName`. Editable in the tile. */
  name: string
  /** Carried across versions, used to auto-match new imports as
   * additional versions of this BCF. Null when the original file
   * didn't declare one. */
  sourceProjectId: string | null
  /** Versions ordered by `importedAt` ascending (oldest first; latest
   * is `versions[versions.length - 1]`). All edits go to the last
   * version; older versions are immutable for Time Machine integrity. */
  versions: BcfVersion[]
  /** Tracks unsaved-since-last-export edits to the latest version. */
  dirty: boolean
  /** Set when this BCF was opened from "Partilhados comigo" (shared from
   *  another workspace): it syncs as `partilha:<id>` with its home
   *  workspace. See docs/PARTILHA.md. */
  partilhaId?: string | null
}

export type TopicPatch = Partial<
  Pick<
    BcfTopic,
    | 'title'
    | 'description'
    | 'status'
    | 'priority'
    | 'type'
    | 'assignedTo'
    | 'dueDate'
    | 'stage'
    | 'labels'
    | 'relatedTopics'
  >
>

/** Catalog rename intent emitted by the catalogs panel when the user
 * opts to propagate label changes to existing topics. `catalog` picks
 * the topic field; `from`/`to` are matched case-insensitively. */
export interface CatalogRename {
  catalog: 'statuses' | 'priorities' | 'types' | 'labels' | 'stages'
  from: string
  to: string
}

export interface BcfClientSlice {
  bcfLoaded: LoadedBcf[]
  bcfActiveId: string | null
  bcfImporting: boolean
  bcfImportError: string | null
  bcfHydrated: boolean

  bcfHydrate: () => Promise<void>
  /** Create a brand new BCF from scratch (no .bcfzip). Useful for
   * starting an issue list before any model exchange. */
  bcfCreateEmpty: (
    name: string,
    opts?: { sourceProjectId?: string | null; partilhaId?: string | null },
  ) => LoadedBcf
  /** Import a `.bcfzip` as a brand new BCF. Returns the new entry. */
  bcfImportFile: (file: File) => Promise<LoadedBcf | null>
  /** Import a `.bcfzip` as a new version of an existing BCF. Returns
   * the BCF entry on success. Drops the oldest version when the cap
   * is hit. */
  bcfImportVersion: (
    targetLocalId: string,
    file: File,
  ) => Promise<LoadedBcf | null>
  /** Try to auto-pair a fresh import to an existing BCF by source
   * project id; falls back to importing as a new BCF. The caller can
   * detect "auto-paired" via the returned `pairedTo` flag and surface
   * an undo / re-route. */
  bcfImportSmart: (
    file: File,
    opts?: {
      /** Quando o ficheiro é do mesmo projeto BCF de um que já está na
       *  biblioteca: `true` junta-o como versão nova, `false` fica como
       *  BCF separado. Sem esta função, junta-se sempre (como antes). */
      versaoNova?: (alvo: { nome: string; topicosActuais: number; topicosDoFicheiro: number }) => boolean
    },
  ) => Promise<{
    entry: LoadedBcf | null
    pairedTo: string | null
  }>
  bcfClose: (localId: string) => void
  bcfRename: (localId: string, name: string) => void
  bcfSetActive: (localId: string | null) => void
  bcfClearError: () => void

  bcfUpdateTopic: (
    localId: string,
    guid: string,
    patch: TopicPatch,
    author: string | null,
  ) => void
  /** Os tópicos da versão mais recente, para a sincronização os ler.
   *  Devolve um array vazio quando não há BCF nenhum com esse id, em
   *  vez de rebentar: sincronizar um BCF que entretanto foi fechado não
   *  é um erro, é não haver nada que fazer. */
  bcfTopicosParaSync: (localId: string) => BcfTopic[]
  /** Escreve o que veio da sincronização.
   *
   *  Substitui os tópicos que vieram e acrescenta os que não existiam;
   *  os que não vierem ficam como estão. Não é um `replace` do conjunto
   *  todo de propósito: o servidor só manda o que mudou, e trocar tudo
   *  apagaria o que só existe cá. */
  bcfAplicarDaSync: (
    localId: string,
    topicos: readonly BcfTopic[],
    apagados: readonly string[],
  ) => void
  bcfCreateTopic: (
    localId: string,
    topic: Omit<
      BcfTopic,
      'guid' | 'index' | 'comments' | 'viewpointCount' | 'viewpointRefs' | 'watchers'
    > & {
      viewpointRefs?: string[]
      watchers?: string[]
      /** GUID escolhido por quem cria (a exportação dos conflitos
       *  precisa de o saber antes, para ligar os tópicos do mesmo grupo
       *  por RelatedTopics). Sem ele, um UUID novo. */
      guid?: string
    },
    author: string | null,
  ) => BcfTopic | null
  bcfAddComment: (
    localId: string,
    guid: string,
    text: string,
    author: string | null,
  ) => void
  bcfDeleteComment: (
    localId: string,
    guid: string,
    commentGuid: string,
  ) => void
  bcfAttachViewpoint: (
    localId: string,
    guid: string,
    viewpointId: string,
  ) => void
  bcfDetachViewpoint: (
    localId: string,
    guid: string,
    viewpointId: string,
  ) => void
  /** Attach a free-form image (PNG/JPG/WebP) to a topic. The image
   * is saved as a `LocalViewpoint` with a neutral camera, so it
   * round-trips into the .bcfzip as an ordinary viewpoint snapshot
   * compatible with BimCollab/Solibri. Returns the viewpoint id on
   * success. */
  bcfAttachImage: (
    localId: string,
    guid: string,
    file: File,
  ) => Promise<string | null>
  /** Replace the per-BCF catalogs (statuses, priorities, types,
   * labels, stages). Used by the catalogs settings panel after the
   * user finishes editing.
   *
   * If `renames` is provided, the caller has decided that the catalog
   * label changes should propagate to the topic strings; every topic
   * whose status / priority / type matches a `from` value (case-
   * insensitive) is rewritten to the new `to` value, and every entry
   * in `topic.labels` is mapped likewise. When `renames` is omitted
   * (or empty), topics keep the old strings; they will appear as
   * overflow values in the dropdowns until the user assigns a new
   * value manually. */
  bcfSetExtensions: (
    localId: string,
    extensions: BcfExtensions,
    renames?: CatalogRename[],
  ) => void
  /** Replace the watchers array of a single topic. Watchers are free
   * strings (names or emails) and aren't validated against the
   * workspace user table since BCF is client-side. */
  bcfSetTopicWatchers: (
    localId: string,
    guid: string,
    watchers: string[],
  ) => void
  bcfMarkClean: (localId: string) => void
  /** O que a lista de tópicos do módulo mostra agora (filtros e blocos
   *  abertos), ou null fora da lista. Só em memória: é o que os pins sobre
   *  o modelo seguem com "Seguir os filtros do módulo BCF". */
  bcfVistaDaLista: VistaDaLista | null
  bcfSetVistaDaLista: (v: VistaDaLista | null) => void
}

/** Os tópicos que a lista do módulo mostra, de um BCF. */
export interface VistaDaLista {
  bcfLocalId: string
  guids: string[]
  /** O que a lista está a filtrar, em palavras (lib/resumoDaLista.ts). */
  resumo?: string[]
}

const INITIAL_STATE = {
  bcfLoaded: [] as LoadedBcf[],
  bcfActiveId: null as string | null,
  bcfImporting: false,
  bcfImportError: null as string | null,
  bcfHydrated: false,
  bcfVistaDaLista: null as VistaDaLista | null,
}

function nowIso(): string {
  return new Date().toISOString()
}

function persist(entries: LoadedBcf[]): void {
  void bcfPersistence.saveAll(entries).catch((err) => {
    console.warn('[bcf] IDB persist failed', err)
  })
}

function latest(bcf: LoadedBcf): BcfVersion {
  return bcf.versions[bcf.versions.length - 1]
}

export function latestVersion(bcf: LoadedBcf): BcfVersion {
  return bcf.versions[bcf.versions.length - 1]
}

export function latestParsed(bcf: LoadedBcf): ParsedBcf {
  return latestVersion(bcf).parsed
}

export function findVersion(
  bcf: LoadedBcf,
  versionId: string,
): BcfVersion | null {
  return bcf.versions.find((v) => v.versionId === versionId) ?? null
}

function withLatestParsed(
  bcf: LoadedBcf,
  fn: (p: ParsedBcf) => ParsedBcf,
): LoadedBcf {
  const lastIdx = bcf.versions.length - 1
  const next = bcf.versions.slice()
  next[lastIdx] = { ...next[lastIdx], parsed: fn(next[lastIdx].parsed) }
  return { ...bcf, dirty: true, versions: next }
}

export const createBcfClientSlice: StateCreator<BcfClientSlice> = (
  set,
  get,
) => ({
  ...INITIAL_STATE,

  bcfHydrate: async () => {
    if (get().bcfHydrated) return
    // Portão da rede de recuperação: nada volta do IndexedDB antes de o
    // utilizador dizer se quer recuperar a sessão anterior. Quando diz
    // que não (ou não havia nada a decidir), o buffer já foi limpo
    // antes desta promessa resolver e a lista vem vazia.
    await whenRecoveryResolved()
    if (get().bcfHydrated) return
    try {
      const rows = await bcfPersistence.loadAll()
      // Tolerate legacy entries (Onda 1-3) that lack `versions`.
      const upgraded: LoadedBcf[] = rows.map((r) => {
        const anyR = r as unknown as {
          versions?: BcfVersion[]
          parsed?: ParsedBcf
          filename?: string
          sizeBytes?: number
          importedAt: number
          localId: string
          name: string
          dirty?: boolean
          sourceProjectId?: string | null
        }
        if (anyR.versions && anyR.versions.length > 0) {
          return comOrigemRetroactiva(r as LoadedBcf)
        }
        const parsed: ParsedBcf | undefined = anyR.parsed
        if (!parsed) return r as LoadedBcf
        const v: BcfVersion = {
          versionId: crypto.randomUUID(),
          label: 'v1',
          filename: anyR.filename ?? `${anyR.name}.bcfzip`,
          importedAt: anyR.importedAt,
          sizeBytes: anyR.sizeBytes ?? 0,
          parsed,
        }
        return {
          localId: anyR.localId,
          name: anyR.name,
          sourceProjectId:
            parsed.sourceProjectId ?? anyR.sourceProjectId ?? null,
          versions: [v],
          dirty: anyR.dirty ?? false,
        }
      })
      upgraded.sort((a, b) => a.versions[0].importedAt - b.versions[0].importedAt)
      set({
        bcfLoaded: upgraded,
        bcfActiveId: upgraded[0]?.localId ?? null,
        bcfHydrated: true,
      })
    } catch (err) {
      console.warn('[bcf] hydrate failed', err)
      set({ bcfHydrated: true })
    }
  },

  bcfCreateEmpty: (name: string, opts) => {
    const trimmed = name.trim() || 'Novo BCF'
    const v: BcfVersion = {
      versionId: crypto.randomUUID(),
      label: 'v1',
      filename: `${trimmed}.bcfzip`,
      importedAt: Date.now(),
      sizeBytes: 0,
      parsed: {
        version: '2.1',
        sourceProjectId: opts?.sourceProjectId ?? crypto.randomUUID(),
        sourceProjectName: trimmed,
        topics: [],
        extensions: defaultExtensions(),
        warnings: [],
      },
    }
    const entry: LoadedBcf = {
      localId: crypto.randomUUID(),
      name: trimmed,
      sourceProjectId: v.parsed.sourceProjectId,
      versions: [v],
      dirty: false,
      ...(opts?.partilhaId ? { partilhaId: opts.partilhaId } : {}),
    }
    const next = [...get().bcfLoaded, entry]
    set({
      bcfLoaded: next,
      bcfActiveId: get().bcfActiveId ?? entry.localId,
    })
    persist(next)
    return entry
  },

  bcfImportSmart: async (file, opts) => {
    set({ bcfImporting: true, bcfImportError: null })
    try {
      const buf = await file.arrayBuffer()
      const result = await parseBcfZip(buf)
      const parsed = result.parsed
      await persistImportedViewpoints(result.importedViewpoints, parsed)
      const match = parsed.sourceProjectId
        ? get().bcfLoaded.find(
            (b) => b.sourceProjectId === parsed.sourceProjectId,
          )
        : undefined
      const juntar =
        match &&
        (opts?.versaoNova?.({
          nome: match.name,
          topicosActuais: latestParsed(match).topics.length,
          topicosDoFicheiro: parsed.topics.length,
        }) ?? true)
      if (match && juntar) {
        const ok = appendVersion(match.localId, file, parsed, get, set)
        return { entry: ok, pairedTo: match.localId }
      }
      const entry = createNewBcf(file, parsed, get, set)
      return { entry, pairedTo: null }
    } catch (err) {
      set({
        bcfImportError:
          err instanceof Error ? err.message : 'Falha a ler o ficheiro BCF.',
      })
      return { entry: null, pairedTo: null }
    } finally {
      set({ bcfImporting: false })
    }
  },

  bcfImportFile: async (file) => {
    set({ bcfImporting: true, bcfImportError: null })
    try {
      const buf = await file.arrayBuffer()
      const result = await parseBcfZip(buf)
      await persistImportedViewpoints(result.importedViewpoints, result.parsed)
      return createNewBcf(file, result.parsed, get, set)
    } catch (err) {
      set({
        bcfImportError:
          err instanceof Error ? err.message : 'Falha a ler o ficheiro BCF.',
      })
      return null
    } finally {
      set({ bcfImporting: false })
    }
  },

  bcfImportVersion: async (targetLocalId, file) => {
    set({ bcfImporting: true, bcfImportError: null })
    try {
      const buf = await file.arrayBuffer()
      const result = await parseBcfZip(buf)
      await persistImportedViewpoints(result.importedViewpoints, result.parsed)
      return appendVersion(targetLocalId, file, result.parsed, get, set)
    } catch (err) {
      set({
        bcfImportError:
          err instanceof Error ? err.message : 'Falha a ler a nova versão.',
      })
      return null
    } finally {
      set({ bcfImporting: false })
    }
  },

  bcfClose: (localId) => {
    const s = get()
    const next = s.bcfLoaded.filter((b) => b.localId !== localId)
    const nextActive =
      s.bcfActiveId === localId ? (next[0]?.localId ?? null) : s.bcfActiveId
    set({ bcfLoaded: next, bcfActiveId: nextActive })
    void bcfPersistence.remove(localId).catch(() => {})
  },

  bcfRename: (localId, name) => {
    const trimmed = name.trim()
    if (!trimmed) return
    const next = get().bcfLoaded.map((b) =>
      b.localId === localId ? { ...b, name: trimmed } : b,
    )
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfSetActive: (localId) => set({ bcfActiveId: localId }),

  bcfClearError: () => set({ bcfImportError: null }),

  bcfTopicosParaSync: (localId) => {
    const bcf = get().bcfLoaded.find((b) => b.localId === localId)
    if (!bcf || bcf.versions.length === 0) return []
    return bcf.versions[bcf.versions.length - 1].parsed.topics
  },

  bcfAplicarDaSync: (localId, topicos, apagados) => {
    if (topicos.length === 0 && apagados.length === 0) return
    const vindos = new Map(topicos.map((t) => [t.guid, t]))
    const fora = new Set(apagados)
    set((state) => ({
      bcfLoaded: state.bcfLoaded.map((b) => {
        if (b.localId !== localId) return b
        return withLatestParsed(b, (p) => {
          const actuais = p.topics
            .filter((t) => !fora.has(t.guid))
            .map((t) => vindos.get(t.guid) ?? t)
          const jaCa = new Set(actuais.map((t) => t.guid))
          const novos = topicos.filter(
            (t) => !jaCa.has(t.guid) && !fora.has(t.guid),
          )
          return { ...p, topics: [...actuais, ...novos] }
        })
      }),
    }))
    // O que veio de fora grava-se como qualquer outra alteração. Sem
    // isto, recarregar a página perdia-o, e o servidor já não o voltava
    // a mandar (a base da sincronização já tinha avançado).
    persist(get().bcfLoaded)
  },

  bcfUpdateTopic: (localId, guid, patch, author) => {
    const ts = nowIso()
    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => ({
        ...p,
        topics: p.topics.map((t) =>
          t.guid === guid
            ? {
                ...t,
                ...patch,
                modifiedDate: ts,
                modifiedAuthor: author ?? t.modifiedAuthor,
              }
            : t,
        ),
      }))
    })
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfCreateTopic: (localId, draft, author) => {
    const ts = nowIso()
    const bcf = get().bcfLoaded.find((b) => b.localId === localId)
    if (!bcf) return null
    const lp = latest(bcf).parsed
    const nextIndex =
      lp.topics.reduce((max, t) => Math.max(max, t.index ?? 0), 0) + 1
    const refs = draft.viewpointRefs ?? []
    const guid =
      draft.guid && !lp.topics.some((t) => t.guid === draft.guid)
        ? draft.guid
        : crypto.randomUUID()
    const newTopic: BcfTopic = {
      guid,
      index: nextIndex,
      title: draft.title,
      description: draft.description ?? null,
      status: draft.status ?? null,
      priority: draft.priority ?? null,
      type: draft.type ?? null,
      assignedTo: draft.assignedTo ?? null,
      creationAuthor: author,
      creationDate: ts,
      modifiedAuthor: null,
      modifiedDate: null,
      dueDate: draft.dueDate ?? null,
      stage: draft.stage ?? null,
      labels: draft.labels ?? [],
      comments: [],
      viewpointCount: refs.length,
      viewpointRefs: refs,
      watchers: draft.watchers ?? [],
      ...(draft.relatedTopics && draft.relatedTopics.length > 0
        ? { relatedTopics: [...new Set(draft.relatedTopics)].filter((g) => g !== guid) }
        : {}),
    }
    const next = get().bcfLoaded.map((b) =>
      b.localId === localId
        ? withLatestParsed(b, (p) => ({ ...p, topics: [...p.topics, newTopic] }))
        : b,
    )
    set({ bcfLoaded: next })
    persist(next)
    return newTopic
  },

  bcfAddComment: (localId, guid, text, author) => {
    const ts = nowIso()
    const comment: BcfComment = {
      guid: crypto.randomUUID(),
      date: ts,
      author,
      comment: text,
      modifiedDate: null,
      modifiedAuthor: null,
      status: null,
    }
    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => ({
        ...p,
        topics: p.topics.map((t) =>
          t.guid === guid
            ? {
                ...t,
                comments: [...t.comments, comment],
                modifiedDate: ts,
                modifiedAuthor: author ?? t.modifiedAuthor,
              }
            : t,
        ),
      }))
    })
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfAttachViewpoint: (localId, guid, viewpointId) => {
    const ts = nowIso()
    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => ({
        ...p,
        topics: p.topics.map((t) => {
          if (t.guid !== guid) return t
          if (t.viewpointRefs.includes(viewpointId)) return t
          const refs = [...t.viewpointRefs, viewpointId]
          return {
            ...t,
            viewpointRefs: refs,
            viewpointCount: refs.length,
            modifiedDate: ts,
          }
        }),
      }))
    })
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfDetachViewpoint: (localId, guid, viewpointId) => {
    const ts = nowIso()
    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => ({
        ...p,
        topics: p.topics.map((t) => {
          if (t.guid !== guid) return t
          const refs = t.viewpointRefs.filter((id) => id !== viewpointId)
          // Como as outras edições, marca a data: é por ela que se sabe
          // que o tópico já não está como veio do ficheiro (ver
          // `origemRetroactiva`).
          return { ...t, viewpointRefs: refs, viewpointCount: refs.length, modifiedDate: ts }
        }),
      }))
    })
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfAttachImage: async (localId, guid, file) => {
    if (!file.type.startsWith('image/')) {
      console.warn('[bcf] attachImage: not an image', file.type)
      return null
    }
    const id = crypto.randomUUID()
    const now = new Date().toISOString()
    const record: LocalViewpoint = {
      id,
      name: file.name.replace(/\.[^.]+$/, '') || 'Imagem',
      notes: '',
      createdAt: now,
      updatedAt: now,
      primaryHash: '',
      modelHashes: [],
      thumbnail: file,
      // Só imagem: "Ver" não mexe na vista (ver semCena em viewpoint.ts).
      viewpoint: { ...neutralViewpoint(), so_imagem: true },
      // Empty ViewpointStateSnapshot: image-only viewpoints don't
      // carry per-element visibility state. Must match the schema in
      // viewpointsStore.ts exactly: the panel reads `state.hidden` /
      // `state.transparent` directly without optional chaining.
      state: {
        hidden: [],
        transparent: [],
        ghostMode: false,
        ghostOpacity: 0.15,
        transparencyOpacity: 0.35,
      },
      folder: null,
      bcfProjectId: localId,
    }
    try {
      await viewpointsStore.save(record)
    } catch (err) {
      console.warn('[bcf] attachImage save failed', err)
      return null
    }
    // Now attach by id, just like a regular viewpoint reference.
    const ts = nowIso()
    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => ({
        ...p,
        topics: p.topics.map((t) => {
          if (t.guid !== guid) return t
          const refs = [...t.viewpointRefs, id]
          return {
            ...t,
            viewpointRefs: refs,
            viewpointCount: refs.length,
            modifiedDate: ts,
          }
        }),
      }))
    })
    set({ bcfLoaded: next })
    persist(next)
    return id
  },

  bcfSetExtensions: (localId, extensions, renames) => {
    const ts = nowIso()
    // Index renames by catalog → Map(fromLowercase → to). The reverse
    // direction is enough because each topic field carries a single
    // string and `topic.labels` carries an array of strings; both
    // resolve via lookup keyed on the lowercased old value.
    const indexByCatalog = new Map<
      CatalogRename['catalog'],
      Map<string, string>
    >()
    for (const r of renames ?? []) {
      if (!indexByCatalog.has(r.catalog))
        indexByCatalog.set(r.catalog, new Map())
      indexByCatalog.get(r.catalog)!.set(r.from.toLowerCase(), r.to)
    }
    const remap = (
      catalog: CatalogRename['catalog'],
      value: string | null,
    ): string | null => {
      if (!value) return value
      const m = indexByCatalog.get(catalog)
      if (!m) return value
      return m.get(value.toLowerCase()) ?? value
    }
    const remapLabels = (labels: string[]): string[] => {
      const m = indexByCatalog.get('labels')
      if (!m) return labels
      return labels.map((l) => m.get(l.toLowerCase()) ?? l)
    }
    const hasRenames = (renames?.length ?? 0) > 0

    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => {
        if (!hasRenames) return { ...p, extensions }
        return {
          ...p,
          extensions,
          topics: p.topics.map((t) => {
            const status = remap('statuses', t.status)
            const priority = remap('priorities', t.priority)
            const type = remap('types', t.type)
            const stage = remap('stages', t.stage)
            const labels = remapLabels(t.labels)
            const changed =
              status !== t.status
              || priority !== t.priority
              || type !== t.type
              || stage !== t.stage
              || labels.some((l, i) => l !== t.labels[i])
            if (!changed) return t
            return {
              ...t,
              status,
              priority,
              type,
              stage,
              labels,
              modifiedDate: ts,
            }
          }),
        }
      })
    })
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfSetTopicWatchers: (localId, guid, watchers) => {
    const ts = nowIso()
    // Drop empty strings, dedupe (case-insensitive on first occurrence),
    // and trim. Free-form input from the panel can carry whitespace and
    // accidental duplicates.
    const cleaned: string[] = []
    const seen = new Set<string>()
    for (const w of watchers) {
      const t = w.trim()
      if (!t) continue
      const key = t.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      cleaned.push(t)
    }
    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => ({
        ...p,
        topics: p.topics.map((t) => {
          if (t.guid !== guid) return t
          return { ...t, watchers: cleaned, modifiedDate: ts }
        }),
      }))
    })
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfSetVistaDaLista: (v) => set({ bcfVistaDaLista: v }),

  bcfMarkClean: (localId) => {
    const next = get().bcfLoaded.map((b) =>
      b.localId === localId ? { ...b, dirty: false } : b,
    )
    set({ bcfLoaded: next })
    persist(next)
  },

  bcfDeleteComment: (localId, guid, commentGuid) => {
    const next = get().bcfLoaded.map((bcf) => {
      if (bcf.localId !== localId) return bcf
      return withLatestParsed(bcf, (p) => ({
        ...p,
        topics: p.topics.map((t) =>
          t.guid === guid
            ? {
                ...t,
                comments: t.comments.filter((c) => c.guid !== commentGuid),
                // Lápide para a sincronização: sem ela o comentário voltava
                // na união com o que está no servidor.
                commentsApagados: [
                  ...new Set([...(t.commentsApagados ?? []), commentGuid]),
                ],
              }
            : t,
        ),
      }))
    })
    set({ bcfLoaded: next })
    persist(next)
  },
})

// ---------- helpers used by the action implementations ----------

type Setter = (
  partial:
    | Partial<BcfClientSlice>
    | ((s: BcfClientSlice) => Partial<BcfClientSlice>),
) => void
type Getter = () => BcfClientSlice

/** Builds a BCF-spec viewpoint with a "neutral" camera. Used both
 * for round-trip of imported viewpoints (when the client lacks an
 * IFC and so cannot reapply the original camera) and for free-form
 * image attachments where there is no camera to capture. The camera
 * is orthographic and centred at origin, which most BCF viewers
 * tolerate without crashing. */
function neutralViewpoint(): Viewpoint {
  return {
    camera: {
      mode: 'orthographic',
      position: { x: 0, y: 0, z: 1 },
      direction: { x: 0, y: 0, z: -1 },
      up: { x: 0, y: 1, z: 0 },
      view_to_world_scale: 1,
    },
    clipping_planes: [],
    components: {
      visible_global_ids: [],
      hidden_global_ids: [],
      selected_global_ids: [],
      default_visibility: true,
    },
    model_refs: [],
  }
}

function inferImageMime(filename: string, fallback = 'image/png'): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
  if (ext === 'png') return 'image/png'
  if (ext === 'webp') return 'image/webp'
  if (ext === 'gif') return 'image/gif'
  return fallback
}

async function persistImportedViewpoints(
  imports: ImportedViewpoint[],
  parsed: ParsedBcf,
): Promise<void> {
  if (imports.length === 0) return
  const now = new Date().toISOString()
  // Os modelos abertos agora, lidos uma vez para todos os viewpoints. O
  // referencial é o da regra comum (o da exportação dos conflitos: um
  // modelo sem MapConversion, senão o mundo), e não o do modelo activo;
  // se a câmara não cair junto dos modelos, tenta-se o de cada modelo
  // (um BCF de outra plataforma nas coordenadas locais de um modelo com
  // MapConversion). Ver bcfReferencial.ts. Sem modelos, a identidade.
  let contexto: ContextoDeLeitura
  try {
    contexto = contextoDaCena()
  } catch {
    contexto = { modelos: [], activoId: null, caixa: null }
  }
  for (const imp of imports) {
    // Já existe (reimportação do mesmo GUID): fica como está, salvo se
    // for um importado antigo, gravado antes de se guardar o .bcfv
    // original e nunca recapturado. Esse recebe a vista lida agora, para
    // a próxima exportação não sair com a câmara neutra.
    const existing = await viewpointsStore.get(imp.id).catch(() => null)
    const antigoSemOriginal =
      !!existing
      && !existing.viewpoint.bcfv_original
      && (existing.viewpoint.components_only === true || semCena(existing.viewpoint))
    if (existing && !antigoSemOriginal) continue
    const thumbnail = imp.snapshotBytes
      ? new Blob([imp.snapshotBytes.slice().buffer], {
          type: inferImageMime(imp.filename),
        })
      : null
    const topic = parsed.topics.find((t) => t.guid === imp.topicGuid)
    // O .bcfv lê-se com a conversão da fronteira (IFC para a cena) e
    // guarda-se tal como veio: enquanto ninguém o recapturar, a
    // exportação reescreve-o sem lhe tocar. A câmara gravada aqui é só
    // a da importação: o "Ver" e os pins relêem o original com os
    // modelos abertos nessa altura (bcfVerImportado.ts, pinAncoraCena.ts).
    const xml = imp.bcfvBytes ? new TextDecoder('utf-8', { ignoreBOM: true }).decode(imp.bcfvBytes) : null
    const lida = xml ? lerVisualizacaoDaCena(xml, [], contexto)?.lida ?? null : null
    const base = neutralViewpoint()
    const viewpoint: Viewpoint = {
      ...base,
      camera: lida?.camera ?? base.camera,
      clipping_planes: lida?.clipping_planes ?? [],
      components: {
        visible_global_ids: lida?.visible_global_ids ?? [],
        hidden_global_ids: lida?.hidden_global_ids ?? [],
        selected_global_ids: lida?.selected_global_ids ?? [],
        default_visibility: lida?.default_visibility ?? true,
      },
      components_only: true,
      ...(xml && lida ? { bcfv_original: { xml, versao: parsed.version } } : {}),
      ...(!lida ? { so_imagem: true } : {}),
    }
    const record: LocalViewpoint = {
      id: imp.id,
      name: topic ? `${topic.title} (anexo)` : imp.filename,
      notes: '',
      createdAt: now,
      updatedAt: now,
      // Imported viewpoints carry no IFC association; they came from
      // someone else's session. Empty primaryHash means "applies to
      // any model". The viewer panel filters by primaryHash so these
      // show up under "todos" but not "modelo activo".
      primaryHash: '',
      modelHashes: [],
      thumbnail,
      viewpoint,
      // Empty ViewpointStateSnapshot: apply does nothing geometry-
      // wise; the value is the snapshot PNG which is what users see.
      // Schema must match viewpointsStore.ts exactly (no optional
      // chaining on the consumer side).
      state: {
        hidden: [],
        transparent: [],
        ghostMode: false,
        ghostOpacity: 0.15,
        transparencyOpacity: 0.35,
      },
      folder: null,
      bcfProjectId: null,
    }
    try {
      await viewpointsStore.save(existing && antigoSemOriginal ? { ...existing, viewpoint } : record)
    } catch (err) {
      console.warn('[bcf] persist imported viewpoint failed', err)
    }
  }
}

/** O nome, ou "nome (2)", "nome (3)"... quando já há um BCF com ele na
 *  biblioteca (por exemplo, um ficheiro do mesmo projeto BCF importado à
 *  parte, em vez de como versão nova). */
export function nomeLivre(nome: string, existentes: readonly string[]): string {
  const usados = new Set(existentes)
  if (!usados.has(nome)) return nome
  let n = 2
  while (usados.has(`${nome} (${n})`)) n++
  return `${nome} (${n})`
}

/** O que veio de um ficheiro, com a origem de cada tópico congelada. É
 *  a base da primeira sincronização de quem ainda não tem base (ver
 *  `bcfOrigem.ts`). */
function comOrigemDoFicheiro(parsed: ParsedBcf): ParsedBcf {
  return { ...parsed, topics: comOrigem(parsed.topics) }
}

/** Um BCF guardado antes de a origem existir: os tópicos que se pode
 *  afirmar estarem como vieram do ficheiro ganham-na agora. */
function comOrigemRetroactiva(bcf: LoadedBcf): LoadedBcf {
  const ultima = bcf.versions[bcf.versions.length - 1]
  if (!ultima) return bcf
  const topics = origemRetroactiva(ultima.parsed.topics, ultima.importedAt)
  if (topics === ultima.parsed.topics) return bcf
  const versions = bcf.versions.slice()
  versions[versions.length - 1] = { ...ultima, parsed: { ...ultima.parsed, topics } }
  return { ...bcf, versions }
}

function createNewBcf(
  file: File,
  parsedDoFicheiro: ParsedBcf,
  get: Getter,
  set: Setter,
): LoadedBcf {
  const parsed = comOrigemDoFicheiro(parsedDoFicheiro)
  const v: BcfVersion = {
    versionId: crypto.randomUUID(),
    label: 'v1',
    filename: file.name,
    importedAt: Date.now(),
    sizeBytes: file.size,
    parsed,
  }
  const entry: LoadedBcf = {
    localId: crypto.randomUUID(),
    name: nomeLivre(
      parsed.sourceProjectName ?? file.name.replace(/\.(bcf|bcfzip)$/i, ''),
      get().bcfLoaded.map((b) => b.name),
    ),
    sourceProjectId: parsed.sourceProjectId,
    versions: [v],
    dirty: false,
  }
  const next = [...get().bcfLoaded, entry]
  set({
    bcfLoaded: next,
    bcfActiveId: get().bcfActiveId ?? entry.localId,
  })
  persist(next)
  return entry
}

function appendVersion(
  targetLocalId: string,
  file: File,
  parsedDoFicheiro: ParsedBcf,
  get: Getter,
  set: Setter,
): LoadedBcf | null {
  const parsed = comOrigemDoFicheiro(parsedDoFicheiro)
  const target = get().bcfLoaded.find((b) => b.localId === targetLocalId)
  if (!target) return null
  const lastLabel = target.versions[target.versions.length - 1]?.label ?? 'v0'
  const m = /^v(\d+)$/.exec(lastLabel)
  const nextNum = m ? Number.parseInt(m[1], 10) + 1 : target.versions.length + 1
  const v: BcfVersion = {
    versionId: crypto.randomUUID(),
    label: `v${nextNum}`,
    filename: file.name,
    importedAt: Date.now(),
    sizeBytes: file.size,
    parsed,
  }
  const versions = [...target.versions, v]
  // Cap: drop oldest until length <= MAX_VERSIONS_PER_BCF.
  while (versions.length > MAX_VERSIONS_PER_BCF) versions.shift()
  const updated: LoadedBcf = {
    ...target,
    versions,
    dirty: false,
    sourceProjectId: target.sourceProjectId ?? parsed.sourceProjectId,
  }
  const next = get().bcfLoaded.map((b) =>
    b.localId === targetLocalId ? updated : b,
  )
  set({ bcfLoaded: next, bcfActiveId: targetLocalId })
  persist(next)
  return updated
}
